import React, { useState } from "react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import { Shield, Check, Plus, Save, X } from "lucide-react";
import { useData } from "../../../context/DataContext";

const RolesPermissionsPage = () => {
  const { systemRoles, updateRole, addRole, deleteRole, logAction } = useData();
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [tempRoleData, setTempRoleData] = useState({});
  const [isCreating, setIsCreating] = useState(false);

  const allPermissions = [
    { key: "group.users", label: "User Management", options: [
        { key: "users.view", label: "View Fans" },
        { key: "users.manage", label: "Manage Fans" },
        { key: "creators.view", label: "View Creators" },
        { key: "creators.manage", label: "Manage Creators" }
    ]},
    { key: "group.finance", label: "Finance & Monetization", options: [
        { key: "finance.view", label: "View Financials" },
        { key: "finance.manage", label: "Manage Financials" },
        { key: "monetization.manage", label: "Manage Opportunities" }
    ]},
    { key: "group.system", label: "System Administration", options: [
        { key: "settings.manage", label: "Manage Settings" },
        { key: "roles.manage", label: "Manage Roles" },
        { key: "support.manage", label: "Manage Support" },
        { key: "moderation.manage", label: "Content Moderation" }
    ]}
  ];

  const handleEdit = (role) => {
    setEditingRoleId(role.id);
    setTempRoleData({ ...role });
  };

  const handleSave = () => {
      if (isCreating) {
          addRole(tempRoleData);
          logAction({
              action: "CREATE_ROLE",
              targetName: tempRoleData.name,
              actorName: "Super Admin",
              details: `Created new role: ${tempRoleData.name}`
          });
          setIsCreating(false);
      } else {
          updateRole(editingRoleId, tempRoleData);
          logAction({
              action: "UPDATE_ROLE",
              targetId: editingRoleId,
              targetName: tempRoleData.name,
              actorName: "Super Admin",
              details: `Updated permissions for ${tempRoleData.name}`
          });
      }
      setEditingRoleId(null);
      setTempRoleData({});
  };

  const handleCancel = () => {
      setEditingRoleId(null);
      setIsCreating(false);
      setTempRoleData({});
  };

  const togglePermission = (permKey) => {
      const currentPerms = tempRoleData.permissions || [];
      if (currentPerms.includes(permKey)) {
          setTempRoleData({ ...tempRoleData, permissions: currentPerms.filter(p => p !== permKey) });
      } else {
          setTempRoleData({ ...tempRoleData, permissions: [...currentPerms, permKey] });
      }
  };

  const startCreate = () => {
      setTempRoleData({ name: "New Role", description: "", permissions: [] });
      setIsCreating(true);
      setEditingRoleId("new");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Roles & Permissions</h1>
          <p className="text-slate-500 mt-1">
            Define roles and assign granular permissions.
          </p>
        </div>
        {!isCreating && <Button onClick={startCreate}><Plus size={16} className="mr-2" /> Create Role</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Creation Card */}
        {isCreating && (
             <Card className="p-6 border-2 border-purple-500 bg-purple-50">
             <div className="mb-4 space-y-3">
                 <input 
                    type="text" 
                    value={tempRoleData.name} 
                    onChange={e => setTempRoleData({...tempRoleData, name: e.target.value})}
                    className="w-full text-xl font-bold bg-transparent border-b border-purple-300 focus:outline-none placeholder-purple-300"
                    placeholder="Role Name"
                    autoFocus
                 />
                 <input 
                    type="text" 
                    value={tempRoleData.description} 
                    onChange={e => setTempRoleData({...tempRoleData, description: e.target.value})}
                    className="w-full text-sm bg-transparent border-b border-purple-200 focus:outline-none text-slate-600"
                    placeholder="Description"
                 />
             </div>
             <div className="mt-4 space-y-4">
                 {allPermissions.map(group => (
                     <div key={group.key}>
                         <h4 className="text-xs font-bold text-purple-700 uppercase mb-2">{group.label}</h4>
                         <div className="grid grid-cols-2 gap-2">
                             {group.options.map(perm => (
                                 <label key={perm.key} className="flex items-center space-x-2 cursor-pointer">
                                     <input 
                                        type="checkbox" 
                                        checked={(tempRoleData.permissions || []).includes(perm.key)}
                                        onChange={() => togglePermission(perm.key)}
                                        className="rounded text-purple-600 focus:ring-purple-500"
                                     />
                                     <span className="text-sm text-slate-700">{perm.label}</span>
                                 </label>
                             ))}
                         </div>
                     </div>
                 ))}
             </div>
             <div className="mt-6 flex gap-3">
                 <Button size="sm" onClick={handleSave}><Save size={14} className="mr-1"/> Save Role</Button>
                 <Button size="sm" variant="ghost" onClick={handleCancel}>Cancel</Button>
             </div>
         </Card>
        )}

        {/* Existing Roles */}
        {systemRoles.map((role) => (
          <Card key={role.id} className={`p-6 transition-all ${editingRoleId === role.id ? 'ring-2 ring-purple-500 shadow-lg scale-[1.02]' : ''}`}>
             {editingRoleId === role.id ? (
                 // EDIT MODE
                 <>
                    <div className="mb-4 space-y-3">
                        <input 
                            type="text" 
                            value={tempRoleData.name} 
                            onChange={e => setTempRoleData({...tempRoleData, name: e.target.value})}
                            className="w-full text-xl font-bold bg-transparent border-b border-slate-300 focus:outline-none"
                        />
                         <input 
                            type="text" 
                            value={tempRoleData.description} 
                            onChange={e => setTempRoleData({...tempRoleData, description: e.target.value})}
                            className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-slate-600"
                        />
                    </div>
                    <div className="mt-4 space-y-4">
                        {allPermissions.map(group => (
                            <div key={group.key}>
                                <h4 className="text-xs font-bold text-slate-400 uppercase mb-2">{group.label}</h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {group.options.map(perm => (
                                        <label key={perm.key} className="flex items-center space-x-2 cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                checked={(tempRoleData.permissions || []).includes(perm.key)}
                                                onChange={() => togglePermission(perm.key)}
                                                className="rounded text-purple-600 focus:ring-purple-500"
                                            />
                                            <span className="text-sm text-slate-700">{perm.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-6 flex justify-between items-center">
                        <div className="flex gap-2">
                            <Button size="sm" onClick={handleSave}><Save size={14} className="mr-1"/> Save Changes</Button>
                            <Button size="sm" variant="ghost" onClick={handleCancel}>Cancel</Button>
                        </div>
                    </div>
                 </>
             ) : (
                 // VIEW MODE
                 <>
                    <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg mr-3">
                        <Shield className="text-purple-600" size={20} />
                        </div>
                        <div>
                        <h3 className="text-lg font-semibold text-slate-800">{role.name}</h3>
                        <p className="text-sm text-slate-500">{role.description}</p>
                        </div>
                    </div>
                    <div>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(role)}>Edit</Button>
                    </div>
                    </div>
                    
                    <div className="mt-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Permissions Overview:</p>
                    <div className="flex flex-wrap gap-2">
                        {role.permissions.map(perm => (
                            <span key={perm} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded border border-slate-200">
                                {allPermissions.flatMap(g => g.options).find(o => o.key === perm)?.label || perm}
                            </span>
                        ))}
                    </div>
                    </div>
                    {!role.isSystem && (
                         <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                              <button 
                                onClick={() => {
                                    deleteRole(role.id);
                                    logAction({
                                        action: "DELETE_ROLE",
                                        targetId: role.id,
                                        targetName: role.name,
                                        actorName: "Super Admin",
                                        details: "Deleted system role"
                                    });
                                }} 
                                className="text-xs text-red-400 hover:text-red-600 flex items-center">
                                  <Trash2 size={12} className="mr-1"/> Delete Role
                              </button>
                         </div>
                    )}
                 </>
             )}
          </Card>
        ))}
      </div>
    </div>
  );
};
// Helper Icon
const Trash2 = ({size, className}) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
);

export default RolesPermissionsPage;
